import { Inject, Injectable } from '@angular/core';
import { UserDefinedFunction } from 'app/model/dataflow/user-defined-function';
import { DataFlowFunctionArgument, IDataFlowFunctionData } from 'app/model/dataflow/dataflow-function-argument';
import { DataFlowDataType } from 'app/model/dataflow/dataflow-datatype';

import { format } from 'app/shared/utils/string.util';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { UserDefinedFunctionLibrary } from 'app/model/dataflow/user-defined-function-library';
import { IDataFlowArgumentData } from 'exp-builder/models/exp-builder.models';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { IResourceContext } from 'shell-models-lib';
import { RESOURCE_CONTEXT_DATA } from 'app/multi-contexts/resource-context.typings';

@Injectable({
    providedIn: 'root'
})
export class UserDefinedFunctionsService {
    public className = 'UserDefinedFunctionsService';
    private _userDefinedFunctions: UserDefinedFunction[] = [];
    private _libraryToFunctionMap: Map<string, string[]> = new Map<string, string[]>();
    private _functionToLibraryMap:  Map<string, string> = new Map<string, string>();
    private _libraryToDataFlowMap:  Map<string, DataFlow> = new Map<string, DataFlow>();

    constructor(@Inject(RESOURCE_CONTEXT_DATA) private readonly _contextData: IResourceContext) {}

    public reset(): void {
        this._userDefinedFunctions = [];
        this._libraryToFunctionMap.clear();
        this._functionToLibraryMap.clear();
        this._libraryToDataFlowMap.clear();
    }

    public addLibrary(library: UserDefinedFunctionLibrary): void {
        let shouldAbort = false;
        if (this._libraryToFunctionMap.has(library.name)) {
            // if we are adding a library that already exists, we likely are refreshing the library so we should clean any old data that was not published
            this.deleteLibrary(library.name);
        }
        library.libraryFunctions.every((func, i1) => {
            // if we are adding a library with a function name that already exists, we want to break from adding the library as it is likely a bad import.
            if (this.hasFunction(func.name)) {
                library.libraryFunctions.forEach((func, i2) => {
                    // remove any previously added functions from the only place they would have been added
                    if (i2 < i1) {
                        this._functionToLibraryMap.delete(func.name);
                    }
                });
                shouldAbort = true;
                return false;
            } else {
                this._functionToLibraryMap.set(func.name, library.name);
                return true;
            }
        });
        if (shouldAbort) {
            return;
        }
        this._libraryToDataFlowMap.set(library.name, library as DataFlow);
        this._userDefinedFunctions.push(...library.libraryFunctions);
        this._libraryToFunctionMap.set(library.name, library.libraryFunctions.map((func) => func.name));
    }

    public editLibrary(library: UserDefinedFunctionLibrary, prevName: string = library.name): void {
        // Guarantee we overwrite changes to functions or library names by deleting the library and it's references and then adding
        this.deleteLibrary(prevName);
        this.addLibrary(library);
    }

    public deleteLibrary(libraryName: string): void {
        // Collect functions to remove, delete the library references, delete the function references
        const functionsToRemove = this._libraryToFunctionMap.get(libraryName);
        this._libraryToFunctionMap.delete(libraryName);
        this._libraryToDataFlowMap.delete(libraryName);
        functionsToRemove?.forEach((func) => this._functionToLibraryMap.delete(func));
        this._userDefinedFunctions = this._userDefinedFunctions.filter((func) => !functionsToRemove?.includes(func.name));
    }

    public getUserDefinedFunctionsForExpressionBuilder(): DataFlowFunctionArgument[] {
        return this._userDefinedFunctions?.map((func) => this.convertToDataFlowFunctionArgument(func));
    }

    public generateUserDefinedFunctionDescriptions(): { [index: string]: IDataFlowArgumentData }  {
        const expressionBuilderDescriptions: { [index: string]: IDataFlowArgumentData } = {};
        this._userDefinedFunctions?.map((func) => this.convertToDataFlowFunctionArgument(func)).forEach((dataFlowArgument) => {
            expressionBuilderDescriptions[dataFlowArgument.name] = {
                icon: dataFlowArgument.getTypeIcon(),
                parametersDescription: dataFlowArgument.getParameterLabels(),
                parameterIcons: dataFlowArgument.getParameterIcons(),
                tooltip: dataFlowArgument.getExpressionTooltip(),
                disabled: false
            };
        });
        return expressionBuilderDescriptions;
    }

    public getFunctionNamesInLibrary(libName: string): string[] {
        return this._libraryToFunctionMap.get(libName);
    }

    public getLibraryNameFromFunction(funcName: string): string {
        return this._functionToLibraryMap.get(funcName);
    }

    public getLibraryAsDataFlow(libName: string): DataFlow {
        return this._libraryToDataFlowMap.get(libName);
    }

    public hasReferenceToLibrary(libName: string, funcList: Set<string>): boolean {
        let hasRefToLib = false;
        if (!funcList) {
            return hasRefToLib;
        }
        funcList.forEach((func) => {
            if (this._functionToLibraryMap.get(func) === libName) {
                hasRefToLib = true;
            }
        });
        return hasRefToLib;
    }

    public hasFunction(funcName: string): boolean {
        return !!this._functionToLibraryMap.get(funcName);
    }

    public hasLibrary(libName: string): boolean {
        return !!this._libraryToFunctionMap.get(libName);
    }

    public updateDataFlowReferences(dataflow: DataFlow): void {
        if (!dataflow) {
            return;
        }
        const udfFunctions = dataflow.getUserDefinedFunctionsUsed();
        dataflow.udfLibraries.clear();
        dataflow.validate(dataflow.owner, { contextData: this._contextData });
        udfFunctions.forEach((func) => dataflow.addUdfLibrary(this.getLibraryAsDataFlow(this.getLibraryNameFromFunction(func))));
    }

    private convertToDataFlowFunctionArgument(func: UserDefinedFunction): DataFlowFunctionArgument {
        const funcArgumentTypes = func.functionArgs.map((arg, index) => `i${index+1} as ${arg.type}` as string);
        const funcDefinitionString = `${func.name}(${funcArgumentTypes.join(', ')}) as ${func.returnType} = ${DataFlowExpressionUtils.wrapExpressionWithEmptyValue(func.funcExpression.expression)}`;
        const funcData: IDataFlowFunctionData = {
            description: format(stringResources.UserDefinedFunctionTooltipDescription, func.owner?.named?.name, funcDefinitionString),
            returnType: func.returnType as DataFlowDataType,
            examples: [],
            parametersDescription: [],
            parametersToAutoComplete: [],
            inputTypes: func.functionArgs.map((arg) => arg.type),
            requiredArguments: func.functionArgs.length,
            functionType: 'General'
        };
        return new DataFlowFunctionArgument(func.name, 'all', undefined, undefined, funcData);
    }
}
